import { Box, Chip, ListItem, Typography } from '@mui/material';
import {
  BoundingBox,
  ChatText,
  Cube,
  FileText,
  HardDrives,
} from '@phosphor-icons/react';
import React from 'react';
import { CustomColorPalette, SearchItemType } from '../types';

interface SearchResultCardProps {
  id: string;
  name: string;
  type: SearchItemType;
  spaceName: string;
  isDarkMode: boolean;
  palette: CustomColorPalette;
  onClick: () => void;
  isLast: boolean;
}

const getTypeIcon = (type: SearchItemType) => {
  switch (type) {
    case 'interface':
      return <FileText size={14} />;
    case 'fluxbot':
      return <ChatText size={14} />;
    case 'object':
      return <Cube size={14} />;
    case 'data':
      return <HardDrives size={14} />;
    default:
      return <BoundingBox size={14} />;
  }
};

const getTypeColor = (
  type: SearchItemType,
  isDarkMode: boolean,
  palette: CustomColorPalette,
) => {
  switch (type) {
    case 'interface':
      return isDarkMode ? palette.blue[400] : palette.blue[600];
    case 'fluxbot':
      return isDarkMode ? palette.neutral[400] : palette.neutral[600];
    default:
      return isDarkMode ? palette.blue[400] : palette.blue[600];
  }
};

export const SearchResultCard: React.FC<SearchResultCardProps> = ({
  name,
  type,
  spaceName,
  isDarkMode,
  palette,
  onClick,
  isLast,
}) => {
  return (
    <ListItem
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        padding: '8px 12px',
        marginBottom: isLast ? 0 : '4px',
        borderRadius: '6px',
        '&:hover': {
          backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[200],
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: '4px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: getTypeColor(type, isDarkMode, palette),
              flexShrink: 0,
            }}
          >
            {getTypeIcon(type)}
          </Box>
          <Typography
            sx={{
              color: isDarkMode ? palette.gray[100] : palette.gray[900],
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: '22px',
          }}
        >
          <Chip
            label={type}
            size='small'
            sx={{
              height: '20px',
              fontSize: '11px',
              fontWeight: 500,
              textTransform: 'capitalize',
              backgroundColor: isDarkMode
                ? palette.gray[700]
                : palette.gray[300],
              color: isDarkMode ? palette.gray[300] : palette.gray[700],
              '& .MuiChip-label': {
                padding: '0 6px',
              },
            }}
          />
          <Typography
            sx={{
              color: isDarkMode ? palette.gray[400] : palette.gray[600],
              fontFamily: 'Roboto',
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {spaceName}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );
};
